import api from './api';
import type { PaginatedResponse } from '@/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type StatementLineStatus = 'reconciled' | 'disputed' | 'unmatched' | 'pending';

export interface StatementLine {
  id: string;
  policy_number: string;
  /** 3c: the insurance company whose statement this line came from. The FK has
   *  always been required on the row; only its id used to be served, so the
   *  table could not name the company. Absent from an older API. */
  insurer_name?: string | null;
  /** U-05: that company's logo; '' when it has none. */
  insurer_logo_url?: string;
  stated_amount: string;
  expected_amount: string | null;
  delta: string | null;
  status: StatementLineStatus;
  statement_date: string;
  source: string | null;
  /** M-02: stored `unmatched`, but stamped with the broker it was uploaded
   *  under -- it will be matched automatically once the policy is issued and
   *  its commission calculated. Shown as "Pending", not "No matching policy".
   *  Absent from an older API, so treat missing as false. */
  awaiting_policy?: boolean;
  /** 3e: how many times a later statement has corrected this line's amount.
   *  0 (or missing, on an older API) means the figure has never moved and
   *  there is no history to open. */
  revision_count?: number;
}

/**
 * 3e: one correction to a statement line.
 *
 * A re-uploaded statement that states a different amount for a policy already
 * on file UPDATES that line rather than adding a second one, so the difference
 * a broker looked at last week can be a different number today. Each of these
 * says how it moved: from → to, what the difference became, who uploaded the
 * statement that changed it, and the NET amount posted to the ledger for it
 * (signed the way the running balance moved -- positive credited the broker).
 */
export interface StatementLineRevision {
  id: string;
  line: string;
  previous_stated_amount: string;
  previous_delta: string | null;
  previous_status: StatementLineStatus | '';
  new_stated_amount: string;
  new_delta: string | null;
  new_status: StatementLineStatus | '';
  ledger_change: string | null;
  statement_date: string | null;
  source: string | null;
  source_file: string;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  created_at: string;
}

/** 3b: one KPI card's bucket — how many lines and how far out they are. */
export interface ReconciliationBucket {
  count: number;
  /** Same orientation as `total_delta` (calculated - received), so the tab
   *  negates it with the same `netDifference` helper.
   *
   *  **null means "nothing to compare"**, not zero: an unmatched line has no
   *  calculated amount, so the Pending and No-matching-policy cards normally
   *  carry no net difference at all. The tab renders that as "—". */
  total_delta: string | null;
}

export interface ReconciliationSummary {
  counts: {
    reconciled: number;
    disputed: number;
    unmatched: number;
    pending: number;
  };
  total_delta: string;
  total_stated: string;
  /** M-02: how many of `counts.unmatched` are awaiting their policy. */
  awaiting_policy?: number;
  /**
   * 3b: the five KPI cards, keyed by the status filter each one applies
   * ("all" clears it). Computed with the status filter REMOVED but insurer and
   * date kept, so clicking a card never collapses the other four to zero.
   * Absent from an older API — the tab falls back to `counts`.
   */
  buckets?: {
    reconciled: ReconciliationBucket;
    disputed: ReconciliationBucket;
    awaiting_policy: ReconciliationBucket;
    no_matching_policy: ReconciliationBucket;
    all: ReconciliationBucket;
  };
}

/** What `POST /commissions/statement-lines/rematch-pending/` did. */
export interface RematchPendingResult {
  checked: number;
  reconciled: number;
  disputed: number;
  unmatched: number;
  failed: number;
  /** Re-read from the table after the run, not inferred from the tallies. */
  still_pending: number;
}

/**
 * One row the importer refused, as the upload endpoint reports it.
 *
 * `row` is the line number in the file the broker is looking at, and `message`
 * is a sentence about their spreadsheet ("\"1.2OO\" is not an amount."). Both
 * are rendered verbatim: a rejected row the user cannot locate is barely
 * better than the silent failure this replaced.
 */
export interface StatementRowError {
  row: number | null;
  policy_number: string;
  message: string;
}

export interface UploadResult {
  /** Rows that became a statement line. */
  processed?: number;
  /** Rows the importer refused. */
  failed?: number;
  /** Data rows found in the file. */
  received?: number;
  reconciled: number;
  disputed: number;
  unmatched: number;
  errors: StatementRowError[];
  lines: StatementLine[];
  line_ids?: string[];
  /** B12: rows the importer recognised as already-imported and left alone --
   *  never re-inserted as a duplicate line. */
  skipped?: number;
  skipped_rows?: StatementRowError[];
  /** An already-imported row whose line was still unmatched or mismatched is
   *  RE-CHECKED against current policies/commission rather than skipped, so
   *  re-uploading a statement is how a broker re-reconciles after fixing a
   *  rate or adding the policy. `status` on each row is the line's status
   *  after the recheck. */
  rechecked?: number;
  rechecked_rows?: (StatementRowError & { status?: string })[];
  /** 3e: rows that CORRECTED the line already on file for that policy number
   *  and statement period, instead of adding a second line for it. */
  updated?: number;
  updated_rows?: {
    row: number | null;
    policy_number: string;
    line_id?: string;
    previous_amount?: string;
    stated_amount?: string;
    status?: string;
    message: string;
  }[];
  /** #436: the layout the file was read as (`digit_policy_xlsb`,
   *  `intermediary_incentive_xlsx`) and its label; absent for the two-column
   *  template. The policy-level layouts are stored, not yet matched. */
  format?: string;
  format_label?: string;
  rows_read?: number;
  rows_imported?: number;
  rows_skipped?: number;
  rows_corrected?: number;
  rows_rejected?: StatementRowError[];
  warnings?: { row: number | null; message: string }[];
  batch_id?: string;
  /** STMT: the insurer + Statement Period this upload's difference report is
   *  read under. Absent from an older API and from a wholly failed upload. */
  difference_report_query?: DifferenceReportQuery;
}

/** STMT: which statement a difference report is about. */
export interface DifferenceReportQuery {
  insurer: string;
  /** `YYYY-MM` — the Statement Period picked on the upload row. */
  period: string;
}

export type DifferenceCategory =
  | 'matched'
  | 'short_paid'
  | 'over_paid'
  | 'not_in_books'
  /** #437: the policy is in our books but has no commission calculated. */
  | 'no_calculation'
  | 'not_in_statement';

/** Amounts are API decimal strings; every difference is `statement - expected`. */
export interface DifferenceTotals {
  count: number;
  expected: string;
  statement: string;
  difference: string;
}

export interface DifferenceReportRow {
  category: DifferenceCategory;
  category_label: string;
  policy_number: string;
  /** Statement date for a statement line; policy date for a missing one. */
  date: string | null;
  /** null: no calculation of ours (in statement, not in our books). */
  expected_amount: string | null;
  /** null: the statement does not list it (in our books, not in statement). */
  statement_amount: string | null;
  difference: string;
  line_id: string | null;
  calculation_id: string | null;
  line_status: StatementLineStatus | 'matched' | null;
  awaiting_policy: boolean;
  /** #437: why this policy landed in this bucket, in words (policy-level rows). */
  reason?: string;
  flags?: string[];
}

/** #444: one month of calculated vs insurer-recorded settlements vs statement. */
export interface SettlementMonthRow {
  /** First day of the month, YYYY-MM-01. */
  month: string;
  calculated: string;
  settled: string;
  statement: string;
  /** settled - calculated */
  settled_minus_calculated: string;
  /** statement - settled */
  statement_minus_settled: string;
  flags: ('settled_not_calculated' | 'calculated_not_settled' | 'settled_differs')[];
  flag_labels: string[];
}

export interface SettlementBlock {
  months: SettlementMonthRow[];
  totals: { calculated: string; settled: string; statement: string };
}

export interface StatementDifferenceReport {
  insurer: string;
  date_from: string;
  date_to: string;
  /** `reconcile_tolerance()` — how far apart still counts as matched. */
  tolerance: string;
  categories: (DifferenceTotals & { key: DifferenceCategory; label: string })[];
  totals: DifferenceTotals;
  rows: DifferenceReportRow[];
  /** #444: absent from an older API. */
  settlements?: SettlementBlock;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const reconciliationService = {
  async listStatementLines(
    params: { page?: number; status?: string } = {},
  ): Promise<PaginatedResponse<StatementLine>> {
    const p = new URLSearchParams({ page: String(params.page ?? 1) });
    if (params.status) p.append('status', params.status);
    const res = await api.get<PaginatedResponse<StatementLine>>(
      `/commissions/statement-lines/?${p}`,
    );
    return res.data;
  },

  /**
   * 3e: the history of one statement line — every correction, newest first.
   *
   * Scoped server-side by the line the caller may already read, so this can
   * only ever return the history of the caller's own broker's line.
   */
  async listStatementLineRevisions(
    lineId: string,
  ): Promise<{ count: number; results: StatementLineRevision[] }> {
    const res = await api.get<{ count: number; results: StatementLineRevision[] }>(
      `/commissions/statement-lines/${lineId}/revisions/`,
    );
    return res.data;
  },

  async getReconciliationSummary(): Promise<ReconciliationSummary> {
    const res = await api.get<ReconciliationSummary>(
      '/commissions/statement-lines/summary/',
    );
    return res.data;
  },

  /**
   * Upload an insurer statement. Only `policy_number` and `stated_amount` are
   * required in the file; `statement_date` is optional and falls back to the
   * chosen statement period (or to today when none was given).
   *
   * An upload where NO row imported answers 400, not 200 — the caller must
   * surface `errors` from either outcome (see `pages/commissions/statementUpload`).
   */
  async uploadStatement(
    file: File,
    insurer?: string,
    statementPeriod?: string,
    options: { format?: string; uploadAgain?: boolean } = {},
  ): Promise<UploadResult> {
    const form = new FormData();
    form.append('file', file);
    // #436: force a statement layout (default: detected from the headers) and
    // knowingly re-send a file already uploaded.
    if (options.format) form.append('format', options.format);
    if (options.uploadAgain) form.append('upload_again', 'true');
    if (insurer) form.append('insurer', insurer);
    // 3d/decision 7: the month the statement covers, as `YYYY-MM`. It fills
    // `statement_date` for rows that carry none; without it the UPLOAD date
    // stood in, so a September statement uploaded in October landed in October.
    if (statementPeriod) form.append('statement_period', statementPeriod);
    const res = await api.post<UploadResult>(
      '/commissions/statement-lines/upload/',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  },

  /**
   * M-02: re-run matching on this broker's lines awaiting a policy. The
   * platform also does this by itself whenever a policy's commission is
   * calculated; this is the "don't wait" button. Broker users only.
   */
  async rematchPending(insurer?: string): Promise<RematchPendingResult> {
    const res = await api.post<RematchPendingResult>(
      '/commissions/statement-lines/rematch-pending/',
      insurer ? { insurer } : {},
    );
    return res.data;
  },

  /**
   * STMT: one statement's differences against our books. Read-only on the
   * server, and scoped to the caller's own broker there.
   */
  async getDifferenceReport(query: DifferenceReportQuery): Promise<StatementDifferenceReport> {
    const p = new URLSearchParams({ insurer: query.insurer, period: query.period });
    const res = await api.get<StatementDifferenceReport>(
      `/commissions/statement-lines/difference-report/?${p}`,
    );
    return res.data;
  },

  /** STMT: the same report as CSV — every line, then the totals. */
  async downloadDifferenceReport(query: DifferenceReportQuery): Promise<Blob> {
    const p = new URLSearchParams({ insurer: query.insurer, period: query.period, format: 'csv' });
    const res = await api.get(`/commissions/statement-lines/difference-report/?${p}`, {
      responseType: 'blob',
    });
    return res.data;
  },

  // The blank template for the statement upload above. Kept beside its upload
  // so the two cannot drift apart; the backend derives the header row from what
  // ReconciliationService.ingest_statement_rows actually reads.
  async downloadStatementTemplate(): Promise<Blob> {
    const res = await api.get('/commissions/statement-lines/csv-template/', {
      responseType: 'blob',
    });
    return res.data;
  },
};
